import mermaid from 'mermaid';

const INIT_DIRECTIVE_REGEX = /^%%\{init:\s*([\s\S]*?)\}%%\s*/i;

// Initialize mermaid with theme
export function initializeMermaid(theme = 'default') {
  mermaid.initialize({
    startOnLoad: false,
    theme: theme === 'dark' ? 'dark' : 'default',
    securityLevel: 'loose',
    maxTextSize: 100000,
    maxEdges: 2000,
    flowchart: {
      useMaxWidth: true,
      htmlLabels: true,
      curve: 'basis'
    },
    sequence: {
      useMaxWidth: true,
      wrap: true
    },
    gantt: {
      useMaxWidth: true
    },
    fontSize: 14,
    logLevel: 'error'
  });
}

// Enhanced validation for mermaid syntax
export function validateMermaidSyntax(code) {
  if (!code || !code.trim()) {
    return { isValid: false, error: 'Empty diagram code' };
  }

  const lines = code.split('\n');
  let firstContentLine = '';
  let diagramType = '';

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine && !trimmedLine.startsWith('%%')) {
      firstContentLine = trimmedLine;
      break;
    }
  }

  if (!firstContentLine) {
    return { isValid: false, error: 'No diagram content found (only comments)' };
  }

  const validStarters = [
    'graph ', 'flowchart ', 'sequencediagram', 'classdiagram',
    'statediagram', 'erdiagram', 'gantt', 'pie ', 'journey', 'gitgraph',
    'c4context', 'c4container', 'c4component', 'c4dynamic', 'mindmap',
    'timeline', 'sankey', 'xychart', 'block', 'packet'
  ];

  const hasValidStarter = validStarters.some(starter => {
    if (firstContentLine.toLowerCase().startsWith(starter.toLowerCase())) {
      diagramType = starter.trim();
      return true;
    }
    return false;
  });

  if (!hasValidStarter) {
    return {
      isValid: false,
      error: 'Unknown diagram type. Please start with a valid diagram declaration (flowchart, graph, sequence, etc.)'
    };
  }

  // Enhanced validation for flowchart/graph diagrams
  if (diagramType === 'graph' || diagramType === 'flowchart') {
    const validation = validateFlowchartSyntax(code);
    if (!validation.isValid) {
      return validation;
    }
  }

  return { isValid: true };
}

// Specific validation for flowchart syntax issues
function validateFlowchartSyntax(code) {
  const lines = code.split('\n');
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line || line.startsWith('%%')) continue;
    
    const lineNumber = i + 1;
    
    // Check for invalid << >> syntax
    if (line.includes('<<') && line.includes('>>')) {
      return {
        isValid: false,
        error: `Line ${lineNumber}: Invalid syntax "<<...>>". Use %% for comments in Mermaid.`,
        suggestion: line.replace(/<<([^>]*)>>/g, '%% $1'),
        lineNumber
      };
    }
    
    // Check for unquoted special characters in node labels
    const nodePattern = /(\w+)\[([^\]]+)\]/g;
    let match;
    
    while ((match = nodePattern.exec(line)) !== null) {
      const label = match[2];
      
      // Skip if already quoted
      if ((label.startsWith('"') && label.endsWith('"')) || 
          (label.startsWith("'") && label.endsWith("'"))) {
        continue;
      }
      
      // Check for problematic characters that should be quoted
      if (hasUnquotedSpecialChars(label)) {
        return {
          isValid: false,
          error: `Line ${lineNumber}: Complex label "${label}" should be quoted. Special characters, spaces, and technical descriptions need quotes.`,
          suggestion: line.replace(match[0], `${match[1]}["${label}"]`),
          lineNumber
        };
      }
    }
    
    // Check for common problematic patterns
    const problematicPatterns = [
      {
        pattern: /\[[^"'\]]*&[^"'\]]*\]/,
        message: "Ampersand (&) in labels should be quoted"
      },
      {
        pattern: /\[[^"'\]]*\([^"'\]]*\)[^"'\]]*\]/,
        message: "Labels with parentheses should be quoted"
      },
      {
        pattern: /\[[^"'\]]*\/[^"'\]]*\]/,
        message: "Labels with forward slashes should be quoted"
      },
      {
        pattern: /\[[^"'\]]*,[^"'\]]*\]/,
        message: "Labels with commas should be quoted"
      }
    ];
    
    for (const { pattern, message } of problematicPatterns) {
      if (pattern.test(line)) {
        return {
          isValid: false,
          error: `Line ${lineNumber}: ${message}`,
          suggestion: `Wrap the label in quotes: ["label text"]`,
          lineNumber
        };
      }
    }
  }
  
  return { isValid: true };
}

// Check if text has special characters that need quoting
function hasUnquotedSpecialChars(text) {
  // Return false if already quoted
  if ((text.startsWith('"') && text.endsWith('"')) || 
      (text.startsWith("'") && text.endsWith("'"))) {
    return false;
  }
  
  // Check for characters that typically cause parsing issues
  const specialChars = /[&()!@#$%^*+={}|\\:";'<>?,./]/;
  
  // Check for spaces (multi-word labels need quotes)
  const hasSpaces = /\s/.test(text);
  
  // Check for technical descriptions (anything with parentheses and content)
  const hasTechnicalDesc = /\([^)]+\)/.test(text);
  
  // Check for paths or complex punctuation
  const hasComplexPunct = /[\/,&-]/.test(text);
  
  return specialChars.test(text) || hasSpaces || hasTechnicalDesc || hasComplexPunct;
}

// Parse mermaid error and provide helpful suggestions
export function parseErrorMessage(error) {
  const errorStr = error.message || error.toString();
  
  // Parse line number from error
  const lineMatch = errorStr.match(/line (\d+)/i);
  const lineNumber = lineMatch ? parseInt(lineMatch[1]) : null;
  
  // Common error patterns and suggestions
  const errorPatterns = [
    {
      pattern: /expecting.*got ['"]?PS['"]?/i,
      message: "Special characters in node labels need quotes",
      suggestion: 'Wrap labels with special characters in quotes: ["Your Label Here"]',
      fix: "Add quotes around node labels containing &, (), or other special characters"
    },
    {
      pattern: /expecting.*SQE.*DOUBLECIRCLEEND.*got/i,
      message: "Invalid flowchart syntax - check node definitions",
      suggestion: "Ensure proper flowchart syntax: A[Label] --> B[Another Label]",
      fix: "Check that all nodes are properly defined with brackets"
    },
    {
      pattern: /parse error/i,
      message: "Diagram syntax error",
      suggestion: "Check for unquoted special characters, mismatched brackets, or invalid syntax",
      fix: "Review diagram syntax and ensure all labels are properly formatted"
    },
    {
      pattern: /identifier.*expected/i,
      message: "Invalid node identifier",
      suggestion: "Node IDs should be alphanumeric (A, B, node1, etc.)",
      fix: "Use simple alphanumeric identifiers for nodes"
    }
  ];
  
  for (const pattern of errorPatterns) {
    if (pattern.pattern.test(errorStr)) {
      return {
        originalError: errorStr,
        userMessage: pattern.message,
        suggestion: pattern.suggestion,
        fix: pattern.fix,
        lineNumber,
        type: 'syntax'
      };
    }
  }
  
  return {
    originalError: errorStr,
    userMessage: "Diagram syntax error",
    suggestion: "Check your diagram syntax and try again",
    fix: "Review the diagram code for syntax errors",
    lineNumber,
    type: 'unknown'
  };
}

// Get diagram SVG for export
export function getDiagramSVG(containerId) {
  const container = document.getElementById(containerId);
  if (container) {
    const svg = container.querySelector('svg');
    if (svg) {
      const clonedSvg = svg.cloneNode(true);
      return clonedSvg.outerHTML;
    }
  }
  return null;
}

function parseInitConfig(rawConfig) {
  try {
    return JSON.parse(rawConfig);
  } catch (err) {
    return null;
  }
}

function buildInitDirective(config) {
  return `%%{init: ${JSON.stringify(config)}}%%\n`;
}

function splitDirective(code) {
  if (!code) {
    return { leadingWhitespace: '', directive: null, rest: '' };
  }

  const leadingWhitespace = code.match(/^\s*/)?.[0] ?? '';
  const trimmed = code.slice(leadingWhitespace.length);
  const match = trimmed.match(INIT_DIRECTIVE_REGEX);

  if (!match) {
    return { leadingWhitespace, directive: null, rest: trimmed };
  }

  const directive = match[0];
  const rest = trimmed.slice(directive.length);

  return {
    leadingWhitespace,
    directive,
    rest
  };
}

export function getThemeVariablesFromCode(code) {
  const { directive } = splitDirective(code);
  if (!directive) {
    return {};
  }

  const match = directive.match(INIT_DIRECTIVE_REGEX);
  if (!match) {
    return {};
  }

  const parsed = parseInitConfig(match[1]);
  if (!parsed || typeof parsed !== 'object') {
    return {};
  }

  if (!parsed.themeVariables || typeof parsed.themeVariables !== 'object') {
    return {};
  }

  return parsed.themeVariables;
}

export function applyThemeVariablesToCode(code, themeVariables) {
  if (!code) {
    return code;
  }

  const cleanedVariables = Object.entries(themeVariables || {})
    .filter(([, value]) => Boolean(value))
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});

  const { leadingWhitespace, directive, rest } = splitDirective(code);
  const existingConfig = directive
    ? parseInitConfig(directive.match(INIT_DIRECTIVE_REGEX)?.[1] ?? '') || {}
    : {};

  const mergedConfig = {
    ...existingConfig,
    theme: 'base',
    themeVariables: {
      ...(existingConfig.themeVariables || {}),
      ...cleanedVariables
    }
  };

  const newDirective = buildInitDirective(mergedConfig);
  const restContent = rest.replace(/^\n*/, '');

  return `${leadingWhitespace}${newDirective}${restContent}`;
}

export function removeThemeVariablesFromCode(code) {
  if (!code) {
    return code;
  }

  const { leadingWhitespace, directive, rest } = splitDirective(code);
  if (!directive) {
    return code;
  }

  const rawConfig = directive.match(INIT_DIRECTIVE_REGEX)?.[1];
  const parsed = rawConfig ? parseInitConfig(rawConfig) : null;

  if (!parsed) {
    return code;
  }

  if (parsed.themeVariables) {
    delete parsed.themeVariables;
  }

  if (Object.keys(parsed).length === 1 && parsed.theme === 'base') {
    const restContent = rest.replace(/^\n*/, '');
    return `${leadingWhitespace}${restContent}`;
  }

  const newDirective = buildInitDirective(parsed);
  const restContent = rest.replace(/^\n*/, '');

  return `${leadingWhitespace}${newDirective}${restContent}`;
}
